import {
  BroadcastIcon,
  CheckCircleIcon,
  ClockIcon,
  UserCircleIcon,
  WarningIcon,
} from "@phosphor-icons/react";
import { Tag, TagProps } from "antd";
import {
  getProjectStatusColor,
  isProjectActionNeeded,
  isProjectErrored,
  isProjectLive,
  isProjectReady,
  mapProjectStatus,
} from "../../helpers/helper";
import { components } from "@libertify/api-types/media";
import { useTranslate } from "@refinedev/core";
import { colors, radius } from "@libertify/ui";
import { useMemo } from "react";

export type ProjectStatusType = components["schemas"]["ProjectStatus"];

export const ProjectStatusTag = ({
  status,
  ...props
}: { status: ProjectStatusType } & TagProps) => {
  const t = useTranslate();
  const iconProps = {
    size: 15,
    style: { verticalAlign: "middle", marginRight: 4 },
  };

  const getTagStatusIcon = (status: ProjectStatusType) => {
    if (isProjectLive(status))
      return <BroadcastIcon weight="fill" {...iconProps} />;
    if (isProjectReady(status))
      return <CheckCircleIcon weight="fill" {...iconProps} />;
    if (isProjectErrored(status)) return <WarningIcon {...iconProps} />;
    if (isProjectActionNeeded(status)) return <UserCircleIcon {...iconProps} />;
    // status in progress when none of the above conditions are met
    return <ClockIcon weight="fill" {...iconProps} />;
  };

  const getTagStatusLabel = (status: ProjectStatusType) => {
    const key = mapProjectStatus(status);
    return t(`media.components.projectQuickAccess.${key}`);
  };

  const tagColor = useMemo(() => {
    const tagStatus = mapProjectStatus(status);
    if (tagStatus === "live" || tagStatus === "ready")
      return colors.primary[700];
    if (tagStatus === "inProgress") return colors.info[700];
    return colors.neutral.white;
  }, [status]);

  return (
    <Tag
      {...props}
      icon={getTagStatusIcon(status)}
      style={{
        backgroundColor: getProjectStatusColor(status),
        color: tagColor,
        borderRadius: radius.pill,
        padding: "4px 12px",
        fontSize: 11,
        fontWeight: 500,
        border: "none",
        width: "fit-content",
        maxWidth: "125px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 4,
        ...props.style,
      }}
    >
      {getTagStatusLabel(status)}
    </Tag>
  );
};
